import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Dialog from "components/dialog";
import SelectFormField from "components/select-form-field";
import { useGetAppointmentTypesQuery } from "services/appointment-types";
import { AppointmentType } from "services/types/appointment-types.type";
import { Patient } from "services/types/patients.type";

interface ScheduleNextAppointmentFormValues {
  appointment_type: number | null;
}

interface ScheduleNextAppointmentModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onScheduleNow: (appointmentTypeId: number) => void;
  readonly patient: Patient | null;
  readonly suggestedAppointmentType: AppointmentType | null;
}

export default function ScheduleNextAppointmentModal({
  open,
  onClose,
  onScheduleNow,
  patient,
  suggestedAppointmentType
}: ScheduleNextAppointmentModalProps) {
  const { data: appointmentTypes = [] } = useGetAppointmentTypesQuery({
    is_active: true
  });

  const appointmentTypeOptions = useMemo(() => {
    return appointmentTypes.map((type) => ({
      value: type.id,
      label: type.name,
      object: type
    }));
  }, [appointmentTypes]);

  const methods = useForm<ScheduleNextAppointmentFormValues>({
    defaultValues: {
      appointment_type: suggestedAppointmentType?.id ?? null
    }
  });

  const { control, watch, reset } = methods;
  const selectedAppointmentTypeId = watch("appointment_type");

  // Reset form when modal opens with a new suggested type
  useEffect(() => {
    if (open && suggestedAppointmentType) {
      reset({
        appointment_type: suggestedAppointmentType.id
      });
    }
  }, [open, suggestedAppointmentType, reset]);

  const selectedAppointmentType = useMemo(() => {
    return appointmentTypes.find(
      (type) => type.id === selectedAppointmentTypeId
    );
  }, [appointmentTypes, selectedAppointmentTypeId]);

  const handleScheduleNow = useCallback(() => {
    if (selectedAppointmentTypeId) {
      onScheduleNow(selectedAppointmentTypeId);
    }
  }, [selectedAppointmentTypeId, onScheduleNow]);

  const handleClose = useCallback(
    (_event: object, _reason: string) => {
      onClose();
    },
    [onClose]
  );

  const patientFullName = patient
    ? `${patient.first_name} ${patient.last_name}`
    : "";

  return (
    <FormProvider {...methods}>
      <Dialog
        title="Schedule next appointment?"
        open={open}
        onClose={handleClose}
        minWidth={500}
        maxWidth="sm"
        dialogContentProps={{
          sx: {
            padding: "24px !important"
          }
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Patient
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {patientFullName}
            </Typography>
          </Box>

          <Box>
            <SelectFormField
              options={appointmentTypeOptions}
              name="appointment_type"
              label="Appointment type"
              control={control}
              required
            />
            {selectedAppointmentType && (
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "2px",
                    backgroundColor: selectedAppointmentType.color,
                    flexShrink: 0
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Duration: {selectedAppointmentType.default_duration}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>

        <DialogActions sx={{ px: 0, pt: 3, pb: 0 }}>
          <Button variant="outlined" onClick={onClose}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleScheduleNow}
            disabled={!selectedAppointmentTypeId}
          >
            Schedule now
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
